import { svelteHelpers } from "@propublica/pp-frontend";

import * as components from "./svelte-components.js";

try {
    // @ts-ignore (TODO(@alecglassford): Fix type problems with components)
    svelteHelpers.hydrateAllComponentsOnPage(components);
} catch (e) {
    console.error("component hydration failed:", e);
}

let soundEnabled = false;

const mediaQuery = window.matchMedia("(prefers-reduced-motion: reduce)");
const desktopMq = window.matchMedia("(min-width: 48em)");
let hiddenPlaying = [];
document.addEventListener("visibilitychange", () => {
    const videos = document.querySelectorAll("video");
    if (document.hidden) {
        hiddenPlaying = [];
        videos.forEach((v) => {
            const vid = /** @type {HTMLVideoElement} */ (v);
            if (!vid.paused) hiddenPlaying.push(vid);
            vid.pause();
        });
        soundEnabled = false;
        applySound();
        syncSoundUI();
    } else {
        hiddenPlaying.forEach((vid) => playClip(vid));
        hiddenPlaying = [];
    }
});

function applyResponsiveVideoSources() {
    document.querySelectorAll("video[data-src-desktop]").forEach((el) => {
        const v = /** @type {HTMLVideoElement} */ (el);
        const want = desktopMq.matches
            ? v.dataset.srcDesktop
            : v.dataset.srcMobile;
        if (!want || v.src === want) return;
        const wasPlaying = !v.paused;
        v.src = want;
        v.load();
        if (wasPlaying) playClip(v);
    });
}
applyResponsiveVideoSources();
desktopMq.addEventListener?.("change", applyResponsiveVideoSources);

function playMutedVideo(v) {
    v.muted = true;
    v.play().catch(() => {
        if (v.dataset.pendingPlay || v.readyState >= 2) return;
        v.dataset.pendingPlay = "1";
        v.addEventListener(
            "canplay",
            () => {
                delete v.dataset.pendingPlay;
                v.play().catch(() => {});
            },
            { once: true },
        );
        v.load();
    });
}

function playClip(video) {
    const v = /** @type {HTMLVideoElement} */ (video);
    v.muted = !soundEnabled;

    const attempt = () =>
        v.play().catch(() => {
            // if blocked, retry muted
            v.muted = true;
            return v.play();
        });

    attempt().catch(() => {
        //wait until the clip is ready, then retry
        if (v.dataset.pendingPlay || v.readyState >= 2) return;
        v.dataset.pendingPlay = "1";
        v.addEventListener(
            "canplay",
            () => {
                delete v.dataset.pendingPlay;
                attempt().catch(() => {});
            },
            { once: true },
        );
        v.preload = "auto";
        v.load();
    });
}

function warmOnApproach(section, video) {
    const warm = new IntersectionObserver(
        (entries) => {
            entries.forEach((entry) => {
                if (!entry.isIntersecting) return;
                video.preload = "auto";
                video.load();
                warm.unobserve(entry.target);
            });
        },
        { rootMargin: "100% 0px" },
    );
    warm.observe(section);
}

function onScrollFrame(fn) {
    let ticking = false;
    const schedule = () => {
        if (ticking) return;
        ticking = true;
        requestAnimationFrame(() => {
            ticking = false;
            fn();
        });
    };
    fn();
    window.addEventListener("scroll", schedule, { passive: true });
    window.addEventListener("resize", schedule);
}

const inlineAutoplayVideos = document.querySelectorAll(
    "video[autoplay]:not(.p-custom-opener__bg-video)",
);
if (inlineAutoplayVideos.length) {
    const inlineObs = new IntersectionObserver(
        (entries) => {
            entries.forEach((entry) => {
                const v = /** @type {HTMLVideoElement} */ (entry.target);
                if (entry.isIntersecting) {
                    v.preload = "auto";
                    playMutedVideo(v);
                } else {
                    v.pause();
                }
            });
        },
        { threshold: 0, rootMargin: "300px 0px" },
    );
    inlineAutoplayVideos.forEach((v) => inlineObs.observe(v));
}

const openerPoster = document.querySelector(".p-custom-opener__poster");
function hideOpenerPoster() {
    openerPoster?.classList.add("is-hidden");
}
function playVisibleOpenerVideo() {
    document.querySelectorAll(".p-custom-opener__bg-video").forEach((el) => {
        const v = /** @type {HTMLVideoElement} */ (el);
        if (v.offsetParent === null) return;
        v.preload = "auto";
        v.addEventListener("playing", hideOpenerPoster, { once: true });
        playMutedVideo(v);
    });
}
playVisibleOpenerVideo();
desktopMq.addEventListener?.("change", playVisibleOpenerVideo);

function resumeOpenerOnGesture() {
    playVisibleOpenerVideo();
}
window.addEventListener("touchstart", resumeOpenerOnGesture, {
    once: true,
    passive: true,
});
window.addEventListener("pointerdown", resumeOpenerOnGesture, { once: true });

const PLAY_ICON =
    '<svg viewBox="0 0 32 32" aria-hidden="true" focusable="false"><path d="M6.66669 6.66666C6.66655 6.19745 6.79021 5.73651 7.0252 5.33038C7.26018 4.92425 7.59817 4.58732 8.00502 4.35359C8.41188 4.11987 8.8732 3.99763 9.34241 3.99922C9.81161 4.00081 10.2721 4.12618 10.6774 4.36266L26.6734 13.6933C27.077 13.9276 27.4122 14.2637 27.6453 14.668C27.8784 15.0723 28.0013 15.5308 28.0017 15.9975C28.0021 16.4642 27.88 16.9229 27.6476 17.3276C27.4152 17.7324 27.0806 18.0691 26.6774 18.304L10.6774 27.6373C10.2721 27.8738 9.81161 27.9992 9.34241 28.0008C8.8732 28.0024 8.41188 27.8801 8.00502 27.6464C7.59817 27.4127 7.26018 27.0757 7.0252 26.6696C6.79021 26.2635 6.66655 25.8025 6.66669 25.3333V6.66666Z" fill="currentColor"/></svg>';

document.querySelectorAll("[data-max-interview]").forEach((section) => {
    const interview = /** @type {HTMLVideoElement | null} */ (
        section.querySelector(".max-interview__video")
    );
    if (!interview) return;

    warmOnApproach(section, interview);

    const media = section.querySelector(".max-interview__player");

    const transcriptEl = section.querySelector(".max-interview__transcript");
    const wordEls = Array.from(
        section.querySelectorAll(
            ".max-interview__transcript .transcript-words .word",
        ),
    );
    /** @type {Array<{ el: HTMLElement, start: number, words: Array<{el: Element, start: number}> }>} */
    const sentences = [];
    const endsSentence = (txt) => /[.?!]["')\]]?$/.test(txt.trim());
    const endsClause = (txt) => /[,;:—–]["')\]]?$/.test(txt.trim());
    const MAX_CHUNK_WORDS = 9;
    const MIN_CLAUSE_WORDS = 4;

    const berryGrid = section.hasAttribute("data-berry-grid");

    let activeSentence = -1;

    function buildLines() {
        const chunk = !desktopMq.matches;
        const lines = document.createElement("div");
        lines.className = "max-interview__lines";
        lines.setAttribute("aria-hidden", "true");
        sentences.length = 0;
        let current = null;
        wordEls.forEach((el) => {
            if (!current) {
                const p = document.createElement("p");
                p.className = "max-interview__sentence";
                lines.appendChild(p);
                current = { el: p, start: 0, words: [] };
                sentences.push(current);
            }
            const start = parseFloat(
                /** @type {HTMLElement} */ (el).dataset.start || "0",
            );
            el.classList.remove("is-revealed");
            current.el.appendChild(el);
            current.el.appendChild(document.createTextNode(" "));
            current.words.push({ el, start });
            const txt = el.textContent || "";
            const n = current.words.length;
            const boundary =
                endsSentence(txt) ||
                (chunk &&
                    (n >= MAX_CHUNK_WORDS ||
                        (endsClause(txt) && n >= MIN_CLAUSE_WORDS)));
            if (boundary) current = null;
        });
        sentences.forEach((s) => (s.start = s.words[0].start));
        transcriptEl
            .querySelectorAll(".transcript-words")
            .forEach((n) => n.remove());
        transcriptEl.querySelector(".max-interview__lines")?.remove();
        transcriptEl.appendChild(lines);
        activeSentence = -1;
    }

    if (wordEls.length && transcriptEl) buildLines();

    const syncWords = () => {
        const t = interview.currentTime;

        let si = -1;
        for (let i = 0; i < sentences.length; i++) {
            if (t >= sentences[i].start) si = i;
            else break;
        }

        if (si !== activeSentence) {
            sentences.forEach((s, i) =>
                s.el.classList.toggle("is-active", i === si),
            );
        }
        if (si >= 0) {
            sentences[si].words.forEach((w) => {
                if (t >= w.start) w.el.classList.add("is-revealed");
            });
        }

        if (si !== activeSentence) {
            activeSentence = si;
            if (berryGrid) {
                if (si === sentences.length - 1) window.__showBerryGrid?.();
                else window.__hideBerryGrid?.();
            }
        }
    };
    if ("requestVideoFrameCallback" in interview) {
        const tick = () => {
            syncWords();
            interview.requestVideoFrameCallback(tick);
        };
        interview.requestVideoFrameCallback(tick);
    } else {
        interview.addEventListener("timeupdate", syncWords);
    }

    if (wordEls.length && transcriptEl) {
        desktopMq.addEventListener?.("change", () => {
            buildLines();
            syncWords();
        });
    }

    const progressFill = /** @type {HTMLElement | null} */ (
        section.querySelector(".max-interview__progress-fill")
    );
    const drawProgress = () => {
        if (!progressFill || !interview.duration) return;
        const p = Math.min(1, interview.currentTime / interview.duration);
        progressFill.style.transform = `scaleX(${p})`;
    };
    interview.addEventListener("timeupdate", drawProgress);

    const replayBtn = /** @type {HTMLButtonElement | null} */ (
        section.querySelector(".max-interview__replay")
    );
    const replayIcon = replayBtn?.querySelector("svg")?.outerHTML ?? "";

    /** @param {string} label */
    const revealPlayButton = (label) => {
        if (!replayBtn) return;
        const icon = label === "Play" ? PLAY_ICON : replayIcon;
        replayBtn.innerHTML = `${icon}<span>${label}</span>`;
        replayBtn.hidden = false;
    };

    interview.addEventListener("ended", () => {
        if (progressFill) progressFill.style.transform = "scaleX(1)";
        revealPlayButton("Replay");
    });

    const resetInterview = () => {
        interview.pause();
        // rewind only if it played; seeking an unloaded clip interrupts the
        // upcoming play()
        if (interview.currentTime > 0) interview.currentTime = 0;
        sentences.forEach((s) => {
            s.el.classList.remove("is-active");
            s.words.forEach((w) => w.el.classList.remove("is-revealed"));
        });
        activeSentence = -1;
        if (progressFill) progressFill.style.transform = "scaleX(0)";
        if (replayBtn) replayBtn.hidden = true;
    };

    replayBtn?.addEventListener("click", () => {
        resetInterview();
        playClip(interview);
    });

    const offerPlayWhenReduced = () => {
        if (interview.currentTime || !interview.paused) return;
        if (mediaQuery.matches) revealPlayButton("Play");
        else resetInterview();
    };
    offerPlayWhenReduced();
    mediaQuery.addEventListener?.("change", offerPlayWhenReduced);

    const playTarget =
        section.querySelector(".max-interview__sticky") || section;
    let wantsPlay = false;
    const playObserver = new IntersectionObserver(
        (entries) => {
            entries.forEach((entry) => {
                if (!entry.isIntersecting) {
                    wantsPlay = false;
                    interview.pause();
                    if (mediaQuery.matches) {
                        resetInterview();
                        revealPlayButton("Play");
                    }
                    return;
                }
                if (mediaQuery.matches) return;
                wantsPlay = true;
                resetInterview();
                playClip(interview);
            });
        },
        { threshold: 0.5 },
    );
    playObserver.observe(playTarget);

    const stage = /** @type {HTMLElement} */ (playTarget);
    const fadeEl = /** @type {HTMLElement} */ (media || stage);
    const FADE_BAND = 0.25;
    const FADE_OUT_START = 0.75;
    const FADE_OUT_END = 1;
    onScrollFrame(() => {
        const rect = section.getBoundingClientRect();
        const vh = window.innerHeight;
        const travel = section.offsetHeight - vh;
        const mrect = fadeEl.getBoundingClientRect();
        const belowCenter = mrect.top + mrect.height / 2 - vh / 2;
        const into = Math.min(
            1,
            Math.max(0, 1 - belowCenter / (vh * FADE_BAND)),
        );
        const p = travel > 0 ? Math.min(1, Math.max(0, -rect.top / travel)) : 0;
        const out = Math.max(
            0,
            Math.min(1, (p - FADE_OUT_START) / (FADE_OUT_END - FADE_OUT_START)),
        );
        const opacity = Math.min(into, 1 - out);
        stage.style.opacity = String(opacity);

        // pauses when fully faded so hidden audio stops then it resumes the video is back on view
        if (mediaQuery.matches) return;
        const faded = out >= 1;
        if (faded && !interview.paused) {
            interview.pause();
        } else if (
            wantsPlay &&
            !faded &&
            interview.paused &&
            !interview.ended
        ) {
            playClip(interview);
        }
    });
});

const soundToggles = document.querySelectorAll("[data-sound-toggle]");

function applySound() {
    document.querySelectorAll("video").forEach((v) => {
        v.muted = !soundEnabled;
    });
}

const openerIntro = /** @type {HTMLElement | null} */ (
    document.querySelector(".p-custom-opener__intro")
);
if (openerIntro) {
    onScrollFrame(() => {
        const fade = Math.max(
            0,
            1 - window.scrollY / (window.innerHeight * 0.4),
        );
        openerIntro.style.opacity = String(fade);
    });
}

document.querySelectorAll(".story-section--statement").forEach((statement) => {
    const inner = /** @type {HTMLElement | null} */ (
        statement.querySelector(".story-section__statement-inner")
    );
    if (!inner) return;

    onScrollFrame(() => {
        const travel = statement.offsetHeight - window.innerHeight;
        const p =
            travel > 0 ? -statement.getBoundingClientRect().top / travel : 0.5;
        inner.classList.toggle("is-visible", p > -0.18 && p < 0.7);
    });
});

function syncSoundUI() {
    soundToggles.forEach((btn) => {
        btn.setAttribute("aria-pressed", String(soundEnabled));
        btn.classList.toggle("is-sound-on", soundEnabled);
        btn.setAttribute(
            "aria-label",
            soundEnabled ? "Disable sound" : "Enable sound",
        );
        const labelText = btn.querySelector(
            ".p-custom-opener__sound-label-text",
        );
        if (labelText)
            labelText.textContent = soundEnabled
                ? "Disable Sound"
                : "Enable Sound";
    });
}

const soundOnChime = new Audio(
    "https://static.propublica.org/projects/graphics/2026-h2a-supply-chain/h2a-supply-chain-button-sound.mp3",
);
soundOnChime.preload = "auto";

soundToggles.forEach((btn) => {
    btn.addEventListener("click", () => {
        soundEnabled = !soundEnabled;
        applySound();
        syncSoundUI();
        const isOpenerButton = btn.classList.contains(
            "p-custom-opener__sound-btn",
        );
        if (soundEnabled && isOpenerButton) {
            soundOnChime.currentTime = 0;
            soundOnChime.play().catch(() => {});
        }
    });
});
